<template>
    <div class="badge" :class="[curVariant, { disabled: isDisabled, hover: isHovered }]">
        <slot />

        <div v-if="isFraction" class="fraction" :class="[{ max: isMaxed }]">
            {{ $n(min) }}
            <div class="slash">/{{ $n(max) }}</div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, watch, inject } from 'vue';
import { GlyphIcon } from 'ihunter-pathfinder-components';
import { ComponentState } from '../composables/useConstants';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: null, // Let it be null so we can inherit from `inject`
    },
    min: {
        type: Number,
        default: null, //set null
    },
    max: {
        type: Number,
        default: null,
    },
    variant: {
        type: String,
        default: 'default',
    },
});

const isMaxed = computed(() => {
    if (props.min && props.max) {
        return props.min >= props.max;
    }
    return false;
});

// Injecting `disabled`, but make sure it's reactive
const inheritedDisabled = inject(
    'disabled',
    computed(() => false),
);
const inheritedVariant = inject(
    'variant',
    computed(() => ComponentState.Default),
);
const inheritedHover = inject('hover', ref(false));

const curVariant = computed(() => inheritedVariant.value ?? props.variant);
const isDisabled = computed(() => inheritedDisabled.value ?? props.disabled);
const isHovered = computed(() => inheritedHover.value ?? false);
const isFraction = computed(() => props.min !== null && props.max !== null);
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.badge {
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    height: 21px;
    @include Pathfinder.Eyebrow-Strong;
    text-indent: 1.44px;
    padding: Pathfinder.$space-75 Pathfinder.$space-100;
    background: var(--surface-glowbox);
    color: var(--on-surface-glowbox);
    border-radius: Pathfinder.$radius-400;
    user-select: none;

    .fraction {
        @include Pathfinder.Eyebrow-Strong;
        display: flex;
        justify-content: center;
        align-items: center;

        .slash {
            @include Pathfinder.Eyebrow;
            color: var(--on-surface-alt);
        }

        &.max {
            color: var(--critical);
        }
    }

    &.success {
        background: var(--on-success-glowbox);
        color: var(--success-glowbox);
    }

    &.error {
        background: var(--on-critical-glowbox);
        color: var(--critical-glowbox);
    }

    &.hover {
        background: var(--on-action-glowbox);
        color: var(--action-glowbox);

        .fraction {
            .slash {
                color: var(--action-glowbox);
            }

            &.max {
                color: var(--critical-alt);
            }
        }
    }

    &.selected {
        background: var(--on-selection-glowbox);
        color: var(--selection-glowbox);
    }

    &.disabled {
        background: var(--on-surface-alt2);
        color: var(--on-surface-alt);

        .fraction {
            color: var(--on-surface-alt);

            .slash {
                color: var(--on-surface-alt);
            }

            &.max {
                color: var(--on-surface-alt);
            }
        }
    }
}
</style>
